import { inject, Injectable } from '@angular/core';
import { limit, orderBy, query, QueryConstraint, setDoc, where } from 'firebase/firestore';
import { CashRegisterStatus } from '@shared';
import { convertToDate } from '@shared/helpers/convert-to-date';
import { firestoreDeepClone } from '@shared/helpers/firestore-deep-clone';
import { ErrorType } from '@shared/models/api-response';
import { Metadata } from '@shared/models/metadata';
import { IReplicableEntity } from '@shared/models/replicable-entity';
import { ReplicationSyncStatus } from '@shared/models/replication-sync-status';
import { SalesOrder } from '@shared/models/sales-order';
import { BaseReplicableService } from './base-replicable.service';
import { CashRegisterService } from './cash-register/cash-register.service';
import { EntityConfig } from './replication/entity-config';

/**
 * The single Firestore source of truth for Order 2.5 sales orders — BOTH
 * offline-created (`UnSynced`, pushed by the pipeline) and online-created
 * (`Synced`, mirrored on save). Replaces the legacy `offlineSalesOrders`
 * collection, which held offline orders only and is no longer read.
 */
export const SALES_ORDERS_V25_COLLECTION = 'salesOrdersV2.5';

/**
 * Replicable service for classic POS sales orders (Order 2.5). Owns the
 * `salesOrdersV2.5` collection — the offline-first source of truth for every
 * order the PWA touches: an order is written to Firestore first, mirrored to
 * the Desktop generic store through the replication batch (the Desktop is a
 * replica only — no dedicated `/SalesOrders` write remains), pushed to the
 * Edara 2 backend by `SalesOrderSyncService` when it is local-first, and
 * restored from the Desktop on recovery.
 *
 * Deliberate deviations from the Order 3.0 twin:
 * - `metadata.localId` always equals `external_id`, so the Firestore doc id,
 *   the Desktop upsert key and the backend idempotency key stay one value.
 * - Every status transition also records the backend outcome
 *   (`metadata.syncInfo.syncStatusCode`, processing stamps, `document_code`,
 *   `require_auto_workorder`) via {@link EntityConfig.additionalStatusFields}:
 *   a 400-class business
 *   rejection is parked for manual re-sync, 500-class failures auto-retry.
 *
 * @see US 27474 — Replicate & Restore (Order 2.5)
 */
@Injectable({
  providedIn: 'root'
})
export class SalesOrderReplicationService extends BaseReplicableService<SalesOrder> {
  protected readonly entityType = 'salesOrder';
  protected readonly endpoint = 'SalesOrders';
  protected readonly firestoreCollection = SALES_ORDERS_V25_COLLECTION;

  protected override readonly replicationEnabled = true;
  protected override readonly defaultSyncStatus = ReplicationSyncStatus.UnSynced;
  // Restored after the cash entities (priority 1) so a recovered order always
  // finds its register/transaction context already in place.
  protected override readonly syncPriority = 2;

  /**
   * Persist an offline-created order locally and replicate it to the Desktop.
   * The record starts `UnSynced`; `SalesOrderSyncService` pushes it to the
   * backend afterwards. Never blocks on the Desktop or the network.
   */
  async createOrder(salesOrder: SalesOrder): Promise<SalesOrder> {
    this.ensureLocalIdentity(salesOrder);
    salesOrder.metadata!.syncInfo = {
      ...(salesOrder.metadata!.syncInfo ?? {}),
      syncStatus: ReplicationSyncStatus.UnSynced
    };
    return this.create(salesOrder);
  }

  /**
   * Persist an updated offline order and replicate the new state so the
   * Desktop mirror stays current (the recovery conflict rule depends on
   * `metadata.updatedDate`). The sync status is left as it is — an `UnSynced`
   * order keeps waiting for the backend push, exactly like the legacy
   * merge-write it replaces.
   */
  async updateOrder(salesOrder: SalesOrder): Promise<SalesOrder> {
    this.ensureLocalIdentity(salesOrder);
    return this.update(salesOrder);
  }

  /**
   * Mirror an order the backend already owns (created or updated online) into
   * the local store and the Desktop replica, stamped `Synced` so the
   * backend-sync pipeline leaves it alone. This is how online orders enter
   * `salesOrdersV2.5` — replacing the old dedicated `POST {native}/SalesOrders`
   * write path. Orders without an `external_id` (created outside the PWA)
   * cannot be keyed and are skipped.
   */
  async mirrorSyncedOrder(salesOrder: SalesOrder): Promise<SalesOrder> {
    if (!salesOrder.external_id) return salesOrder;

    this.trimCashRegister(salesOrder);
    this.ensureLocalIdentity(salesOrder);
    salesOrder.metadata!.createdBy = salesOrder.metadata!.createdBy ?? this.getSanitizedUserProfile();
    salesOrder.metadata!.organizationInfo = salesOrder.metadata!.organizationInfo ?? this.getOrganizationInfo();
    salesOrder.metadata!.syncInfo = {
      ...(salesOrder.metadata!.syncInfo ?? {}),
      syncStatus: ReplicationSyncStatus.Synced,
      syncedDate: new Date(),
      syncedBy: this.getSanitizedUserProfile()
    };
    return this.update(salesOrder);
  }

  /**
   * Mirror a backend-accepted update onto the order's EXISTING document. An
   * order edited online can carry a different `external_id` than its stored
   * document (the legacy tags update had the same problem), so the linked
   * document is resolved by external_id → document_code → paper_number first;
   * writing under a fresh id would leave a stale duplicate behind. With no
   * linked document the order is mirrored under its own `external_id`.
   */
  async mirrorSyncedLinkedOrder(salesOrder: SalesOrder): Promise<SalesOrder> {
    if (!salesOrder.external_id && !salesOrder.document_code && !salesOrder.paper_number) {
      return salesOrder;
    }

    const linked = await this.findLinkedOrder(salesOrder);
    if (linked?.external_id) {
      salesOrder.external_id = linked.external_id;
      // Preserve the stored document's lifecycle fields the online editor
      // does not know about.
      salesOrder.is_locked = salesOrder.is_locked ?? linked.is_locked;
      salesOrder.is_approved = salesOrder.is_approved ?? linked.is_approved;
      salesOrder.is_issued = salesOrder.is_issued ?? linked.is_issued;
      salesOrder.metadata = salesOrder.metadata ?? linked.metadata;
    }
    return this.mirrorSyncedOrder(salesOrder);
  }

  /**
   * Resolve the stored document for an order: by `external_id`, then by
   * `document_code`, then by `paper_number` — the same fallback chain the
   * legacy `offlineSalesOrders` lookups used.
   */
  async findLinkedOrder(salesOrder: Partial<SalesOrder>): Promise<SalesOrder | undefined> {
    if (salesOrder.external_id) {
      const byId = await this.getFromFirestore(salesOrder.external_id);
      if (byId) return byId;
    }
    if (salesOrder.document_code) {
      const byCode = await this.findByField('document_code', salesOrder.document_code);
      if (byCode) return byCode;
    }
    if (salesOrder.paper_number) {
      return this.findByField('paper_number', salesOrder.paper_number);
    }
    return undefined;
  }

  /**
   * The original push selection, in its original order and budget: pending
   * orders newest first, then — the Service Worker only, never the main-thread
   * fallback — rejected orders due for another attempt, then stale claims,
   * `maxRecords` in total (the old `getPendingOrProcessingSalesOrders(1000)`).
   */
  async selectForPushAsync(
    includeRejected: boolean,
    maxRecords = 1000,
    olderThanMs = 10 * 60 * 1000
  ): Promise<SalesOrder[]> {
    let remaining = maxRecords;
    const unsynced = await this.getUnsyncedAsync(remaining);
    remaining -= unsynced.length;
    const rejected = includeRejected && remaining > 0
      ? await this.getRetryableFailedAsync(olderThanMs, remaining)
      : [];
    remaining -= rejected.length;
    const stale = remaining > 0 ? await this.getStaleSyncingAsync(olderThanMs, remaining) : [];
    return [...unsynced, ...rejected, ...stale];
  }

  /**
   * Orders that still need to be pushed to the backend (Part 2 — Sync):
   * this cashier's, newest first, as the original `Pending` query read them.
   */
  async getUnsyncedAsync(maxRecords = 1000): Promise<SalesOrder[]> {
    return this.queryOrders([
      ...this.ownedByCashier(ReplicationSyncStatus.UnSynced),
      orderBy('document_date', 'desc'),
      limit(maxRecords)
    ]);
  }

  /**
   * Rejected orders due for another automatic attempt — the original
   * `ConflictOrError` query, clause for clause: a server-side failure
   * (`syncInfo.syncStatusCode` 500) whose attempt started (`processingStartedAt`)
   * more than `olderThanMs` ago, oldest attempt first. A business rejection
   * (400) is never retried automatically — it waits for the manual re-sync —
   * and, exactly as with the original query, an order with no recorded code
   * or no start stamp does not match. Without that gate a backend that keeps
   * rejecting the same orders turned every sync trigger — one per sale — into
   * a Failed → Syncing → Failed rewrite of the whole backlog (Bug 27923).
   */
  async getRetryableFailedAsync(olderThanMs = 10 * 60 * 1000, maxRecords = 1000): Promise<SalesOrder[]> {
    return this.queryOrders([
      ...this.ownedByCashier(ReplicationSyncStatus.Failed),
      where('metadata.syncInfo.syncStatusCode', '==', 500),
      where('metadata.syncInfo.processingStartedAt', '<', new Date(Date.now() - olderThanMs)),
      orderBy('metadata.syncInfo.processingStartedAt'),
      limit(maxRecords)
    ]);
  }

  /**
   * Stale `Syncing` claims left behind by an interrupted push (e.g. a crash
   * between "mark Syncing" and the backend response) — the original
   * `Processing` query: claims whose attempt started more than `olderThanMs`
   * ago, oldest first. The 10-minute default is the original in-flight grace
   * period, and it matches the Service Worker's 10-minute request timeout, so
   * a chunk still in flight is never re-claimed.
   */
  async getStaleSyncingAsync(olderThanMs = 10 * 60 * 1000, maxRecords = 1000): Promise<SalesOrder[]> {
    return this.queryOrders([
      ...this.ownedByCashier(ReplicationSyncStatus.Syncing),
      where('metadata.syncInfo.processingStartedAt', '<', new Date(Date.now() - olderThanMs)),
      orderBy('metadata.syncInfo.processingStartedAt'),
      limit(maxRecords)
    ]);
  }

  async getByLocalIdAsync(localId: string): Promise<SalesOrder | undefined> {
    return this.getFromFirestore(localId);
  }

  /**
   * Persist a record recovered from the Desktop into Firestore without
   * re-emitting a replication event (it already exists on the Desktop). A full
   * replace, unlike the merge the live update path uses: the recovery conflict
   * rule already decided the Desktop copy wins outright.
   */
  async restoreToFirestore(salesOrder: SalesOrder): Promise<void> {
    salesOrder.external_id = salesOrder.external_id ?? salesOrder.metadata?.localId;
    this.normalizeDates(salesOrder);
    const documentRef = this.firestoreService.doc(
      `${this.firestoreCollection}/${this.getDocumentId(salesOrder)}`
    );
    const plainObject = firestoreDeepClone(salesOrder);
    await this.firestoreService.settleWrite(
      () => setDoc(documentRef, plainObject),
      documentRef.path
    );
  }

  /**
   * Adds the recovery scope for already-`Synced` orders and the backend
   * outcome fields on top of the standard config.
   */
  protected override getEntityConfig(): EntityConfig {
    return {
      ...super.getEntityConfig(),
      createSyncedRestoreFilter: () => this.createOpenClosureFilter(),
      // Restores go through this service so the record lands with real Date
      // values (the Desktop returns ISO strings) — the grid filters and the
      // closure calculation query those fields with ranges.
      saveRestoredEntity: entity => this.restoreToFirestore(entity as SalesOrder),
      additionalStatusFields: (entity, status, statusMessage) =>
        this.buildSyncOutcomeFields(entity as SalesOrder, status, statusMessage)
    };
  }

  /**
   * Sales orders carry Date fields (`document_date`, installment due dates)
   * that are queried with range and `orderBy` filters, so they must reach
   * Firestore as Timestamps — `firestoreDeepClone` preserves `Date` instances,
   * unlike the base implementation's JSON round-trip.
   *
   * Written with `merge: true`, matching the legacy update path: a form update
   * racing a background sync must not clobber the writeback-only fields
   * (`document_code`, `syncInfo.syncStatusCode`, `is_approved`, ...).
   * For a create the document does not exist yet, so the merge is a full write.
   */
  protected override async saveToFirestore(salesOrder: SalesOrder): Promise<void> {
    this.normalizeDates(salesOrder);
    const documentRef = this.firestoreService.doc(
      `${this.firestoreCollection}/${this.getDocumentId(salesOrder)}`
    );
    const plainObject = firestoreDeepClone(salesOrder);
    await this.firestoreService.settleWrite(
      () => setDoc(documentRef, plainObject, { merge: true }),
      documentRef.path
    );
  }

  /**
   * Guarantee the replication seam: `metadata.localId` is pinned to
   * `external_id` and the sync state defaults to `UnSynced`. Author, tenant,
   * creation stamp and device/environment info are filled by the base
   * `ensureValidMetadata` afterwards when still missing.
   */
  private ensureLocalIdentity(salesOrder: SalesOrder): void {
    if (!salesOrder.external_id) {
      throw new Error('Sales order External Id cannot be null or empty');
    }

    const metadata = salesOrder.metadata ?? new Metadata();
    metadata.localId = salesOrder.external_id;
    if (!metadata.syncInfo?.syncStatus) {
      metadata.syncInfo = {
        ...(metadata.syncInfo ?? {}),
        syncStatus: ReplicationSyncStatus.UnSynced
      };
    }

    salesOrder.metadata = metadata;
  }

  /**
   * The backend-outcome fields written atomically with every status
   * transition (the SyncStatusManager writes the status, message,
   * `updatedDate` and `syncedDate` itself): the HTTP-class outcome that
   * drives the retry policy, the processing stamps the detail views show,
   * and the `document_code` and `require_auto_workorder` the backend decided on
   * acceptance (both stamped on the in-memory order by the sync service before
   * the status is applied; the flag gates the SO lock automatic actions). Also
   * mutates the in-memory `syncInfo` so the Desktop-replicated payload matches
   * what was persisted.
   */
  private buildSyncOutcomeFields(
    salesOrder: SalesOrder,
    status: ReplicationSyncStatus,
    statusMessage?: string
  ): Record<string, unknown> {
    const now = new Date();
    const syncInfo: any = salesOrder.metadata!.syncInfo ?? (salesOrder.metadata!.syncInfo = { syncStatus: status });
    const fields: Record<string, unknown> = {};

    switch (status) {
      case ReplicationSyncStatus.Syncing:
        fields['metadata.syncInfo.processingStartedAt'] = now;
        syncInfo.processingStartedAt = now;
        break;
      case ReplicationSyncStatus.Synced:
        fields['metadata.syncInfo.syncStatusCode'] = 200;
        fields['metadata.syncInfo.processingCompletedAt'] = now;
        syncInfo.syncStatusCode = 200;
        syncInfo.processingCompletedAt = now;
        if (salesOrder.document_code) {
          fields['document_code'] = salesOrder.document_code;
        }
        if (typeof salesOrder.require_auto_workorder === 'boolean') {
          fields['require_auto_workorder'] = salesOrder.require_auto_workorder;
        }
        break;
      case ReplicationSyncStatus.Failed: {
        const statusCode = this.getErrorStatusCode(statusMessage);
        fields['metadata.syncInfo.syncStatusCode'] = statusCode;
        fields['metadata.syncInfo.processingCompletedAt'] = now;
        syncInfo.syncStatusCode = statusCode;
        syncInfo.processingCompletedAt = now;
        break;
      }
      default: // UnSynced roll-back — nothing beyond what the manager writes.
        break;
    }

    return fields;
  }

  /** Same 400-vs-500 split the legacy status writeback used. */
  private getErrorStatusCode(statusMessage?: string): number {
    return statusMessage?.startsWith(ErrorType.BadRequest) ? 400 : 500;
  }

  /**
   * Recovery scope for orders the backend already accepted: only those in the
   * cash register's CURRENTLY OPEN closure are worth restoring — they still
   * feed the open-closure totals and receipts. Anything older is history the
   * backend owns. With no open register there is no open closure, so no
   * `Synced` order is restored.
   */
  private async createOpenClosureFilter(): Promise<(entity: IReplicableEntity) => boolean> {
    const cashRegister = await this.findOpenCashRegister();
    if (!cashRegister) return () => false;

    const registerId = cashRegister.id;
    const openedAtMs = this.toMillis(cashRegister.opening_details?.opening_date);

    return (entity: IReplicableEntity) => {
      const order = entity as SalesOrder;
      if (registerId && order.cashRegister?.id && order.cashRegister.id !== registerId) {
        return false;
      }
      if (!openedAtMs) return true;
      const orderAtMs = this.toMillis(order.document_date ?? order.metadata?.createdDate);
      return orderAtMs >= openedAtMs;
    };
  }

  /**
   * The cash register store. Read through the facade rather than the Desktop:
   * after Phase 2 nothing writes the Desktop's register document, so a Desktop
   * read would silently go stale and mis-scope this restore.
   */
  private readonly cashRegisterService = inject(CashRegisterService);

  /** The current cashier's register, only when it is open. */
  private async findOpenCashRegister(): Promise<any | undefined> {
    const email = this.authService.currentUserEmail;
    if (!email) return undefined;

    const cashRegister = await this.cashRegisterService.findByActivatedUserAsync(email);
    return cashRegister?.status === CashRegisterStatus.Open ? cashRegister : undefined;
  }

  private async findByField(
    field: 'document_code' | 'paper_number',
    value: string
  ): Promise<SalesOrder | undefined> {
    const queryRef = query(
      this.firestoreService.col<SalesOrder>(this.firestoreCollection),
      where('metadata.organizationInfo.id', '==', this.authService.currentTenant),
      where(field, '==', value),
      limit(1)
    );
    const [salesOrder] = await this.firestoreService.getCollectionAsync<SalesOrder>(queryRef);
    return salesOrder;
  }

  /**
   * The register's opening/closing details are heavyweight and irrelevant on
   * an order document — the legacy write path stripped them too.
   */
  private trimCashRegister(salesOrder: SalesOrder): void {
    if (salesOrder.cashRegister) {
      delete salesOrder.cashRegister.opening_details;
      delete salesOrder.cashRegister.closing_details;
    }
  }

  /** The tenant + cashier + status equalities every push query starts with. */
  private ownedByCashier(status: ReplicationSyncStatus): QueryConstraint[] {
    return [
      where('metadata.organizationInfo.id', '==', this.authService.currentTenant),
      where('metadata.createdBy.email', '==', this.authService.userProfile?.email),
      where('metadata.syncInfo.syncStatus', '==', status)
    ];
  }

  /**
   * Run a push query. These carry an `orderBy` (and a range on the same field),
   * so each shape needs its composite index deployed — the same indexes the
   * pre-cutover queries had on the old collection.
   */
  private async queryOrders(constraints: QueryConstraint[]): Promise<SalesOrder[]> {
    const results = await this.firestoreService.getCollectionAsync<SalesOrder>(
      query(this.firestoreService.col<SalesOrder>(this.firestoreCollection), ...constraints)
    );
    results.forEach(order => this.normalizeDates(order));
    return results;
  }

  /**
   * Normalize the order's date fields to real `Date` instances, in both
   * directions: Firestore hands back `Timestamp`s (the backend sync payload
   * expects dates), and the Desktop hands back ISO strings (Firestore must
   * store Timestamps, because the orders grid filters and sorts these fields
   * with range queries). Unparseable values are left untouched rather than
   * turned into an Invalid Date.
   */
  private normalizeDates(salesOrder: SalesOrder): void {
    salesOrder.document_date = this.toValidDate(salesOrder.document_date) ?? salesOrder.document_date;
    if (salesOrder.fulfillment_date) {
      salesOrder.fulfillment_date = this.toValidDate(salesOrder.fulfillment_date) ?? salesOrder.fulfillment_date;
    }
    salesOrder.salesOrder_installments?.forEach(installment => {
      installment.due_date = this.toValidDate(installment.due_date) ?? installment.due_date;
    });
    salesOrder.salesOrder_details?.forEach((item: any) => {
      if (item?.expiry_date) {
        item.expiry_date = this.toValidDate(item.expiry_date) ?? item.expiry_date;
      }
    });
    const metadata: any = salesOrder.metadata;
    if (metadata) {
      for (const field of ['createdDate', 'updatedDate']) {
        if (metadata[field]) {
          metadata[field] = this.toValidDate(metadata[field]) ?? metadata[field];
        }
      }
      for (const field of ['syncedDate', 'processingStartedAt', 'processingCompletedAt']) {
        if (metadata.syncInfo?.[field]) {
          metadata.syncInfo[field] = this.toValidDate(metadata.syncInfo[field]) ?? metadata.syncInfo[field];
        }
      }
      for (const field of ['replicationDate', 'restoreDate']) {
        if (metadata.replicationInfo?.[field]) {
          metadata.replicationInfo[field] = this.toValidDate(metadata.replicationInfo[field]) ?? metadata.replicationInfo[field];
        }
      }
    }
  }

  /** A valid `Date` for Timestamp/ISO-string/Date input, otherwise undefined. */
  private toValidDate(value: unknown): Date | undefined {
    if (!value) return undefined;
    const date = convertToDate(value as any);
    return date instanceof Date && !isNaN(date.getTime()) ? date : undefined;
  }

  /**
   * Normalize a date that may be a Firestore Timestamp, an ISO string, or a
   * Date into epoch milliseconds (0 when absent/invalid).
   */
  private toMillis(value: unknown): number {
    if (!value) return 0;
    const date = convertToDate(value as any);
    const time = date instanceof Date ? date.getTime() : NaN;
    return isNaN(time) ? 0 : time;
  }
}
